import { ReactElement, ReactNode } from "react"
import _ from "lodash"
import { ThemeOptions } from "@mui/material"
import { deepmerge } from "@mui/utils"
import { Route, RouteProps } from "react-router-dom"

enum RouteTypes {
  Map = "map",
  Page = "page",
  Base = "base",
}

export interface IRouteConfigOptions {
  // Controls the visibility of the App Navigation in AppLayout. (default: true)
  showAppLayoutNav?: boolean
}
export interface IRouteConfig extends Omit<RouteProps, "children"> {
  options?: IRouteConfigOptions
  key?: string
  type?: RouteTypes
  children?: IRouteConfig[]
}
export interface IRouteGroups {
  mapRoutes: IRouteConfig[]
  pageRoutes: IRouteConfig[]
  baseRoutes: IRouteConfig[]
}

// The menu configuration utilized in the app's service configuration.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type MergeRoutes = (
  sourceRoutes: IRouteConfig[],
  extensionRoutes: IRouteConfig[]
) => IRouteGroups

type RegisterRouteByConfig = (
  routeConfig: IRouteConfig,
  index: number
) => ReactElement

type GroupRouteConfigByType = (routeConfig: IRouteConfig[]) => IRouteGroups

type MergeRouteConfig = (
  soruceRouteConfig: IRouteConfig[],
  extensionRouteConfig: IRouteConfig[]
) => IRouteConfig[]

type ThemeOptionFactory = (theme: ThemeOptions) => ThemeOptions

interface ThemeOptionFactories {
  mobile?: ThemeOptionFactory
  "kiosk-h"?: ThemeOptionFactory
  "kiosk-v"?: ThemeOptionFactory
}

interface ExtensionConfig {
  themeOptionFactories: ThemeOptionFactories
  ignoreAppThemeFactory?: boolean
  ignoreDeviceThemeFactory?: boolean
  appLayoutConfig?: IDeviceAppLayoutConfig
  appComponents?: IDeviceAppComponentConfig
  routes: IRouteConfig[]
}

type GetAllExtensionRoutes = (
  extensionList: string[],
  extensionConfigs: ExtensionConfig[] | object
) => IRouteConfig[]

export interface IAppLayoutConfig {
  search?: JSX.Element | null
  appNavigation?: JSX.Element | null
}
export interface IDeviceAppLayoutConfig {
  mobile?: IAppLayoutConfig
  "kiosk-h"?: IAppLayoutConfig
  "kiosk-v"?: IAppLayoutConfig
}
export interface IAppComponentConfig {
  preservedView?: JSX.Element | null
  loadingScreen?: JSX.Element | null
}
export interface IDeviceAppComponentConfig {
  mobile?: IAppComponentConfig
  "kiosk-h"?: IAppComponentConfig
  "kiosk-v"?: IAppComponentConfig
}

export type GetExtensionAppLayoutConfig = (
  extensionList: string[],
  extensionConfigs: ExtensionConfig[] | object,
  deviceConfig?: string
) => IAppLayoutConfig

export type GetExtensionAppComponentConfig = (
  extensionList: string[],
  extensionConfigs: ExtensionConfig[] | object,
  deviceConfig?: string
) => IAppComponentConfig

export const getAllExtensionRoutes: GetAllExtensionRoutes = (
  extensionList = [],
  extensionConfigs = {},
  deviceConfig = "mobile"
) => {
  if (_.isEmpty(extensionList)) return []

  return _(extensionList)
    .map((extensionName) => {
      // Retrieve rotue config by extension name and the device mode. (kiosk-v, kiosk-h, mobile)
      const routes = _.get(extensionConfigs, [
        extensionName,
        "routes",
        deviceConfig,
      ])
      if (_.isNil(routes) || _.isEmpty(routes)) return null
      return routes
    })
    .compact()
    .flatten()
    .value()
}

export const getExtensionThemeFactories = (
  extensionList = [],
  extensionConfigs = {},
  deviceConfig = "mobile"
) => {
  const result = {
    themeFactories: [],
    ignoreAppThemeFactory: false,
    ignoreDeviceThemeFactory: false,
  }

  if (_.isEmpty(extensionList)) return result

  let ignoreAppThemeFactory = false
  let ignoreDeviceThemeFactory = false

  const themeFactories = _(extensionList)
    .map((extensionName) => {
      const {
        themeOptionFactories,
        ignoreAppThemeFactory: shouldIgnoreAppTheme,
        ignoreDeviceThemeFactory: shouldIgnoreDeviceTheme,
      } = _.get(extensionConfigs, extensionName, {
        ignoreAppThemeFactory: false,
        ignoreDeviceThemeFactory: false,
        themeOptionFactories: {},
      })
      const themeOptionFactory = _.get(themeOptionFactories, deviceConfig)
      if (shouldIgnoreAppTheme) ignoreAppThemeFactory = shouldIgnoreAppTheme

      if (shouldIgnoreDeviceTheme)
        ignoreDeviceThemeFactory = shouldIgnoreDeviceTheme

      if (_.isNil(themeOptionFactory) || !_.isFunction(themeOptionFactory))
        return null
      return themeOptionFactory
    })
    .compact()
    .value()

  return { themeFactories, ignoreAppThemeFactory, ignoreDeviceThemeFactory }
}

const DEFAULT_APP_LAYOUT_CONFIG = {
  search: null,
  appNavigation: null,
}

export const getExtensionAppLayoutConfig: GetExtensionAppLayoutConfig = (
  extensionList = [],
  extensionConfigs = {},
  deviceConfig = "mobile"
) => {
  if (_.isEmpty(extensionList)) return DEFAULT_APP_LAYOUT_CONFIG

  return _(extensionList)
    .map((extensionName) => {
      const { appLayoutConfig } = _.get(extensionConfigs, extensionName, {
        appLayoutConfig: {
          mobile: DEFAULT_APP_LAYOUT_CONFIG,
          "kiosk-h": DEFAULT_APP_LAYOUT_CONFIG,
          "kiosk-v": DEFAULT_APP_LAYOUT_CONFIG,
        },
      })
      const deviceLayoutFactories = _.get(appLayoutConfig, deviceConfig)
      return _.merge({}, DEFAULT_APP_LAYOUT_CONFIG, deviceLayoutFactories)
    })
    .reduce((deviceAppLayoutConfig, appLayoutConfig) => {
      const { search: currSearchComp, appNavigation: currAppNavigationComp } =
        deviceAppLayoutConfig
      const { search, appNavigation } = appLayoutConfig
      return {
        search: currSearchComp || search,
        appNavigation: currAppNavigationComp || appNavigation,
      }
    }, DEFAULT_APP_LAYOUT_CONFIG)
}

const DEFAULT_APP_COMP_CONFIG = {
  preservedView: null,
  loadingScreen: null,
}

export const getExtensionAppComponentConfig: GetExtensionAppComponentConfig = (
  extensionList = [],
  extensionConfigs = {},
  deviceConfig = "mobile"
) => {
  if (_.isEmpty(extensionList)) return DEFAULT_APP_COMP_CONFIG

  return _(extensionList)
    .map((extensionName) => {
      const { appComponents } = _.get(extensionConfigs, extensionName, {
        appComponents: {
          mobile: DEFAULT_APP_COMP_CONFIG,
          "kiosk-h": DEFAULT_APP_COMP_CONFIG,
          "kiosk-v": DEFAULT_APP_COMP_CONFIG,
        },
      })
      const deviceLayoutFactories = _.get(appComponents, deviceConfig)
      return _.merge({}, DEFAULT_APP_COMP_CONFIG, deviceLayoutFactories)
    })
    .reduce(
      (
        deviceAppCompConfig: IAppComponentConfig,
        appCompConfig: IAppComponentConfig
      ) => {
        const {
          loadingScreen: currLoadingScreen,
          preservedView: currPreservedView,
        } = deviceAppCompConfig
        const { loadingScreen, preservedView } = appCompConfig
        return {
          preservedView: currPreservedView || preservedView,
          loadingScreen: currLoadingScreen || loadingScreen,
        }
      },
      DEFAULT_APP_COMP_CONFIG
    )
}

const groupRouteConfigByType: GroupRouteConfigByType = (routeConfigs) => {
  return _.reduce(
    routeConfigs,
    (acc, { type = RouteTypes.Page, ...newRouteConfig }, key) => {
      const routeTypeKey = `${type}Routes`
      const prevRouteConfig = _.get(acc, routeTypeKey)
      if (prevRouteConfig)
        _.set(acc, `${type}Routes`, [...prevRouteConfig, newRouteConfig])
      return acc
    },
    { mapRoutes: [], pageRoutes: [], baseRoutes: [] }
  )
}

const parseRouteConfigToPathObj = (routeConfigs) =>
  _.keyBy(routeConfigs, "path")

// Convert Route Config Array -> Path Object (with 'children' property)
const parseRouteConfigsToPathObjs = (routeConfigs) => {
  const transformedChildren = routeConfigs.map(
    ({ children: childrenProp, ...rest }) => {
      return {
        ...rest,
        children: childrenProp
          ? parseRouteConfigsToPathObjs(childrenProp)
          : undefined,
      }
    }
  )

  return parseRouteConfigToPathObj(transformedChildren)
}

const parsePathObjToRouteConfig = (pathObj) => _.values(pathObj)

// Convert Path Object -> Route Config Array (with 'children' property)
const parsePathObjsToRouteConfigs = (pathObjs) => {
  return parsePathObjToRouteConfig(pathObjs).map(
    ({ children: childrenProp, ...rest }) => {
      return {
        ...rest,
        children: childrenProp
          ? parsePathObjsToRouteConfigs(childrenProp)
          : undefined,
      }
    }
  )
}

const mergeRouteConfigByPath: MergeRouteConfig = (
  sourceRouteConfig = [],
  extensionRouteConfig = []
) => {
  // Step 1: Convert route config to path object with nested routes in 'children' prop.
  const sourcePathObjs = parseRouteConfigsToPathObjs(sourceRouteConfig)
  const extensionPathObjs = parseRouteConfigsToPathObjs(extensionRouteConfig)

  // Step 2: Merge path objects.
  const mergedPathObjs = deepmerge(sourcePathObjs, extensionPathObjs)

  // Step 3: Convert merged path objects to route config array with nested routes in 'children' prop.
  return parsePathObjsToRouteConfigs(mergedPathObjs)
}

export const mergeRoutes: MergeRoutes = (
  sourceRoutes = [],
  extensionRoutes = []
) => {
  // # Step 1: Grouping `extension` route configurations by route type (map or page)
  const {
    mapRoutes: extensionMapRoutes,
    pageRoutes: extensionPageRoutes,
    baseRoutes: extensionBaseRoutes,
  } = groupRouteConfigByType(extensionRoutes)

  // # Step 2: Grouping `source` route configurations by route type (map or page)
  const {
    mapRoutes: sourceMapRoutes,
    pageRoutes: sourcePageRoutes,
    baseRoutes: sourceBaseRoutes,
  } = groupRouteConfigByType(sourceRoutes)

  // # Step 3: Override the 'source' route configurations with the 'extension' route configurations and then return the grouped route object.
  return {
    mapRoutes: mergeRouteConfigByPath(sourceMapRoutes, extensionMapRoutes),
    pageRoutes: mergeRouteConfigByPath(sourcePageRoutes, extensionPageRoutes),
    baseRoutes: mergeRouteConfigByPath(sourceBaseRoutes, extensionBaseRoutes),
  }
}

export const registerRouteByConfig: RegisterRouteByConfig = (
  { children, key: keyProp, path, ...props },
  index
) => {
  const key = keyProp || `extension-route-${path || index}`
  return (
    <Route
      {...props}
      path={path}
      key={key}
      children={
        (children
          ? (children as IRouteConfig[]).map(registerRouteByConfig)
          : children) as ReactNode
      }
    />
  )
}
